import React, { useContext, useState, useEffect, useMemo, useRef } from "react";
import api from "../services/api";
import { AuthContext } from "../context/AuthContext";
import { SurveyContext } from "../context/SurveyContext";
import {
  FaHeart,
  FaRegHeart,
  FaRetweet,
  FaRegComment,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
  FaShare,
  FaBookmark,
  FaRegBookmark,
  FaSmile,
} from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import MentionInput from "./MentionInput";
import TypingIndicator from "./TypingIndicator";
import EmojiPicker from "./EmojiPicker";
import { getProfilePicture } from "../utils/avatar";

const INITIAL_COMMENT_DISPLAY = 3;
const FEED_COMMENT_PREVIEW = 2;
const COMMENT_MAX_CHARS = 280;

const Message = ({ message, onInteraction, onVisibilityChange, showAllByDefault = false, feedMode = false, highlightCommentId = null }) => {
  const { user } = useContext(AuthContext);
  const { condition } = useContext(SurveyContext);
  const navigate = useNavigate();
  const [newComment, setNewComment] = useState("");
  const [comments, setComments] = useState(message.comments);
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [showComments, setShowComments] = useState(showAllByDefault);
  const [showRepostModal, setShowRepostModal] = useState(false);
  const [repostComment, setRepostComment] = useState("");
  const [showAllComments, setShowAllComments] = useState(!!highlightCommentId); // Expand all when highlighting
  const [isLiked, setIsLiked] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [replyingToComment, setReplyingToComment] = useState(null);
  const [commentReply, setCommentReply] = useState("");
  const [highlightedId, setHighlightedId] = useState(highlightCommentId);
  const [showCommentEmojis, setShowCommentEmojis] = useState(false);
  const highlightRef = useRef(null);
  const articleRef = useRef(null);

  // Viewport-based impression tracking
  useEffect(() => {
    if (!onVisibilityChange || !articleRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        onVisibilityChange(message._id, entry.isIntersecting);
      },
      { threshold: 0 }
    );

    observer.observe(articleRef.current);
    return () => observer.disconnect();
  }, [message._id, onVisibilityChange]);

  useEffect(() => {
    setIsLiked(message.likes.includes(user.userId));
  }, [message.likes, user.userId]);

  useEffect(() => {
    setComments(message.comments);
  }, [message.comments]);

  useEffect(() => {
    setShowComments(showAllByDefault);
    // Don't auto-expand all comments - keep pagination
  }, [showAllByDefault]);

  // Scroll to highlighted comment after render
  useEffect(() => {
    if (highlightCommentId && highlightRef.current) {
      // Small delay to ensure DOM is fully rendered
      const timer = setTimeout(() => {
        highlightRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
      // Clear highlight after animation
      const clearTimer = setTimeout(() => setHighlightedId(null), 4000);
      return () => { clearTimeout(timer); clearTimeout(clearTimer); };
    }
  }, [highlightCommentId, comments]);

  const handleLike = async (e) => {
    e.stopPropagation();
    if (!user || !user.userId) {
      console.error("User or user ID is not available");
      return;
    }

    setIsAnimating(true);
    setIsLiked(!isLiked);

    try {
      const response = await api.post(`/messages/${message._id}/like`, {
        userId: user.userId,
        condition,
      });
      const newLikeStatus = response.data.liked;
      const newLikeCount = response.data.likeCount;
      setIsLiked(newLikeStatus);

      if (onInteraction) {
        onInteraction(
          newLikeStatus ? "like" : "unlike",
          message.author._id,
          message._id,
          newLikeCount
        );
      }
    } catch (error) {
      console.error("Error toggling like:", error);
      setIsLiked(!isLiked);
    } finally {
      setTimeout(() => setIsAnimating(false), 300);
    }
  };

  const handleRepost = async (e) => {
    e.stopPropagation();
    setShowRepostModal(true);
  };

  const submitRepost = async () => {
    if (!user || !user.userId) {
      console.error("User or user ID is not available");
      return;
    }
    try {
      await api.post(`/messages/${message._id}/repost`, {
        userId: user.userId,
        content: repostComment,
      });
      // Repost interaction is logged server-side in the /repost route (records
      // metadata.repostedMessageId). Do NOT also call onInteraction("repost", ...)
      // here — that would create a duplicate Interaction row.
      setShowRepostModal(false);
      setRepostComment("");
    } catch (error) {
      console.error("Error reposting message:", error);
    }
  };

  // Append an emoji to the comment, respecting the comment length cap.
  const insertCommentEmoji = (emoji) => {
    setNewComment((prev) =>
      (prev + emoji).length <= COMMENT_MAX_CHARS ? prev + emoji : prev
    );
  };

  const handleComment = async () => {
    if (!user || !user.userId) {
      console.error("User or user ID is not available");
      return;
    }
    try {
      const response = await api.post(`/comments`, {
        messageId: message._id,
        content: newComment,
        condition,
      });
      if (onInteraction) {
        onInteraction("comment", message.author._id);
      }
      const commentWithAuthor = {
        ...response.data,
        author: { username: user.username, _id: user.userId, avatarSeed: user.avatarSeed },
      };
      setComments([...comments, commentWithAuthor]);
      setNewComment("");
      setShowCommentInput(false);
      setShowComments(true);
      // Scroll to the new comment after render
      setTimeout(() => {
        const el = document.getElementById(`comment-${commentWithAuthor._id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 150);
    } catch (error) {
      console.error("Error posting comment:", error);
    }
  };

  const handleCommentLike = async (commentId) => {
    if (!user || !user.userId) {
      console.error("User or user ID is not available");
      return;
    }
    try {
      const response = await api.post(`/comments/${commentId}/like`, {
        userId: user.userId,
        condition,
      });
      const updatedComment = response.data;
      setComments(
        comments.map((c) => (c._id === commentId ? updatedComment : c))
      );

      const interactionType = updatedComment.likes.includes(user.userId)
        ? "like"
        : "unlike";

      if (onInteraction) {
        onInteraction(
          interactionType,
          updatedComment.author._id,
          commentId,
          updatedComment.likes.length
        );
      }
    } catch (error) {
      console.error("Error liking comment:", error);
    }
  };

  const handleCommentReply = async (parentCommentId) => {
    if (!user || !user.userId) {
      console.error("User or user ID is not available");
      return;
    }
    if (!commentReply.trim()) return;

    try {
      const response = await api.post(`/comments`, {
        messageId: message._id,
        content: commentReply,
        parentCommentId: parentCommentId,
        condition,
      });

      if (onInteraction) {
        onInteraction("comment", response.data.author._id);
      }

      setCommentReply("");
      setReplyingToComment(null);

      const commentWithAuthor = {
        ...response.data,
        author: { username: user.username, _id: user.userId, avatarSeed: user.avatarSeed }
      };

      const updatedComments = [...comments, commentWithAuthor];
      setComments(updatedComments);
    } catch (error) {
      console.error("Error posting comment reply:", error);
    }
  };

  const handlePostClick = (e) => {
    // Don't navigate if clicking on interactive elements
    if (e.target.closest('button') || e.target.closest('a') || e.target.closest('textarea') || e.target.closest('input')) {
      return;
    }
    navigate(`/messages/${message._id}?condition=${condition}`);
  };

  const handleCommentButtonClick = (e) => {
    e.stopPropagation();
    if (showAllByDefault || feedMode) {
      // On detail page or feed with inline mode, toggle the input
      setShowCommentInput(!showCommentInput);
      if (!showCommentInput) {
        setShowComments(true);
      }
    } else {
      // On feed without feedMode, navigate to detail page
      navigate(`/messages/${message._id}?condition=${condition}`);
    }
  };

  const handleExpandComments = (e) => {
    e.stopPropagation();
    if (!showComments) {
      // Track comment thread expansion
      if (onInteraction) {
        onInteraction("expand_comments", message._id, message._id);
      }
      if (feedMode) {
        setShowComments(true);
        setShowCommentInput(true);
      } else {
        setShowComments(true);
      }
    } else {
      setShowComments(false);
      setShowCommentInput(false);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInSeconds = Math.floor((now - time) / 1000);

    if (diffInSeconds < 60) return `${diffInSeconds}s`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d`;
    return time.toLocaleDateString();
  };

  const renderTextWithLinks = (text) => {
    if (!text) return text;

    const parts = text.split(/(#[a-zA-Z0-9_]+|@[a-zA-Z0-9_\.]+)/g);

    return parts.map((part, index) => {
      if (part.startsWith('#')) {
        return (
          <button
            key={index}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/search?q=${encodeURIComponent(part)}&type=all`);
            }}
            className="text-spiders-accent-400 hover:text-spiders-accent-300 hover:underline font-medium transition-colors"
          >
            {part}
          </button>
        );
      } else if (part.startsWith('@')) {
        const username = part.slice(1);
        return (
          <button
            key={index}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/profile/${username}?condition=${condition}`);
            }}
            className="text-spiders-primary-400 hover:text-spiders-primary-300 hover:underline font-medium transition-colors"
          >
            {part}
          </button>
        );
      } else {
        return <span key={index}>{part}</span>;
      }
    });
  };

  const organizeComments = (comments) => {
    const commentMap = {};
    const rootComments = [];

    comments.forEach(comment => {
      commentMap[comment._id] = { ...comment, replies: [] };
    });

    comments.forEach(comment => {
      if (comment.parentCommentId && commentMap[comment.parentCommentId]) {
        commentMap[comment.parentCommentId].replies.push(commentMap[comment._id]);
      } else {
        rootComments.push(commentMap[comment._id]);
      }
    });

    return rootComments;
  };

  // Build list of mentionable users (post author + all commenters)
  const mentionableUsers = useMemo(() => {
    const users = [];
    const seen = new Set();

    // Add post author
    if (message.author?.username && !seen.has(message.author.username)) {
      users.push({
        username: message.author.username,
        _id: message.author._id,
      });
      seen.add(message.author.username);
    }

    // Add all commenters
    comments.forEach((comment) => {
      if (comment.author?.username && !seen.has(comment.author.username)) {
        users.push({
          username: comment.author.username,
          _id: comment.author._id,
        });
        seen.add(comment.author.username);
      }
    });

    // Remove current user from mention list (you don't mention yourself)
    return users.filter((u) => u.username !== user.username);
  }, [message.author, comments, user.username]);

  const organizedComments = organizeComments(comments);
  const commentLimit = feedMode && !showAllComments ? FEED_COMMENT_PREVIEW : INITIAL_COMMENT_DISPLAY;
  const displayedComments = showAllComments
    ? organizedComments
    : organizedComments.slice(0, commentLimit);

  const toggleComments = (e) => {
    e.stopPropagation();
    setShowAllComments(!showAllComments);
  };

  const renderComment = (comment, depth = 0) => {
    const isHighlighted = comment._id === highlightedId;
    return (
      <div
        key={comment._id}
        id={`comment-${comment._id}`}
        ref={isHighlighted ? highlightRef : undefined}
        className={`${depth > 0 ? 'mt-2' : ''} ${isHighlighted ? 'comment-highlight rounded-lg' : ''}`}
      >
        <div className={`flex space-x-3 py-3 ${depth > 0 ? 'pl-4' : ''}`}>
          <Link
            to={`/profile/${comment.author?.username}`}
            className="flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={getProfilePicture(comment.author?.username || "unknown", comment.author?.avatarSeed)}
              alt={comment.author?.username}
              className="w-8 h-8 rounded-full hover:opacity-90 transition-opacity border border-spiders-border"
            />
          </Link>
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-1 mb-1">
              <Link
                to={`/profile/${comment.author?.username}`}
                className="hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="font-bold text-spiders-text-primary text-sm">
                  {comment.author?.username || "Unknown"}
                </span>
              </Link>
              <span className="text-spiders-text-muted text-xs">
                @{comment.author?.username}
              </span>
              <span className="text-spiders-text-muted text-xs">·</span>
              <span className="text-spiders-text-muted text-xs">
                {formatTimeAgo(comment.timestamp)}
              </span>
            </div>
            <p className="text-spiders-text-primary text-sm leading-relaxed mb-2">{renderTextWithLinks(comment.content)}</p>
            <div className="flex items-center space-x-4">
              <button
                className={`flex items-center space-x-1.5 text-xs transition-colors duration-200 ${
                  (comment.likes || []).includes(user.userId)
                    ? "text-spiders-error"
                    : "text-spiders-text-muted hover:text-spiders-error"
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCommentLike(comment._id);
                }}
              >
                {(comment.likes || []).includes(user.userId) ? (
                  <FaHeart className="w-3 h-3" />
                ) : (
                  <FaRegHeart className="w-3 h-3" />
                )}
                <span>{(comment.likes || []).length}</span>
              </button>
            </div>
          </div>
        </div>

        {comment.replies && comment.replies.length > 0 && (
          <div className="ml-8 border-l-2 border-spiders-border pl-4">
            {comment.replies.map(reply => renderComment(reply, depth + 1))}
          </div>
        )}
      </div>
    );
  };


  // Activity pulse: posts with comments in the last 3 minutes
  const isActive = useMemo(() => {
    if (!comments || comments.length === 0) return false;
    const threeMinutesAgo = Date.now() - 3 * 60 * 1000;
    return comments.some(
      (c) => c.timestamp && new Date(c.timestamp).getTime() > threeMinutesAgo
    );
  }, [comments]);

  // Unique commenters for avatar stack
  const uniqueCommenters = useMemo(() => {
    if (!comments || comments.length < 2) return [];
    const seen = new Set();
    const result = [];
    for (const c of comments) {
      const username = c.author?.username;
      if (username && !seen.has(username)) {
        seen.add(username);
        result.push({ username, avatarSeed: c.author?.avatarSeed });
        if (result.length >= 5) break;
      }
    }
    return result;
  }, [comments]);

  return (
    <article
      ref={articleRef}
      className="group relative"
      onClick={handlePostClick}
    >
      <div className={`px-4 py-3 border-b border-spiders-border hover:bg-spiders-bg-elevated/30 transition-colors duration-150 cursor-pointer ${isActive ? 'activity-pulse' : ''}`}>
        {/* Repost indicator */}
        {message.repostOf && (
          <div className="flex items-center mb-2 ml-12 text-spiders-text-muted text-sm">
            <FaRetweet className="mr-2 w-3.5 h-3.5" />
            <span className="font-medium">{message.author.username} reposted</span>
          </div>
        )}

        {/* Main content */}
        <div className="flex space-x-3">
          {/* Avatar */}
          <Link
            to={`/profile/${encodeURIComponent(message.author?.username)}?condition=${condition}`}
            className="flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={getProfilePicture(message.author?.username || "unknown", message.author?.avatarSeed)}
              alt={message.author?.username}
              className="w-10 h-10 rounded-full transition-opacity duration-150 hover:opacity-80"
            />
          </Link>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-center flex-wrap gap-x-1.5 gap-y-0.5 mb-1">
              <Link
                to={`/profile/${encodeURIComponent(message.author?.username)}?condition=${condition}`}
                className="hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="font-bold text-spiders-text-primary hover:text-spiders-primary-300 transition-colors">
                  {message.author?.username || "Unknown"}
                </span>
              </Link>
              <span className="text-spiders-text-muted text-sm">
                @{message.author?.username || "Unknown"}
              </span>
              <span className="text-spiders-text-muted">·</span>
              <span
                className="text-spiders-text-muted text-sm hover:underline"
                title={new Date(message.timestamp).toLocaleString()}
              >
                {formatTimeAgo(message.timestamp)}
              </span>
            </div>

            {/* Post content */}
            <div className="text-spiders-text-primary text-[15px] leading-normal whitespace-pre-wrap">
              {renderTextWithLinks(message.content)}
            </div>

            {/* Reposted content */}
            {message.repostOf && (
              <div className="mt-3 p-3 rounded-xl border border-spiders-border bg-spiders-bg-surface/50">
                <div className="flex items-center space-x-2 mb-2">
                  <img
                    src={getProfilePicture(message.repostOf.author?.username || "unknown")}
                    alt={message.repostOf.author?.username}
                    className="w-5 h-5 rounded-full"
                  />
                  <span className="font-medium text-spiders-text-primary text-sm">
                    {message.repostOf.author?.username}
                  </span>
                  <span className="text-spiders-text-muted text-xs">
                    @{message.repostOf.author?.username}
                  </span>
                </div>
                <p className="text-spiders-text-secondary text-sm">
                  {renderTextWithLinks(message.repostOf.content)}
                </p>
              </div>
            )}

            {/* Action Bar */}
            <div className="flex items-center mt-3 -ml-2">
              {/* Comment/Reply Button */}
              <button
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-spiders-text-muted hover:text-spiders-primary-400 hover:bg-spiders-primary-500/10 transition-colors mr-1"
                onClick={handleCommentButtonClick}
              >
                <FaRegComment className="w-4 h-4" />
                {comments.length > 0 && (
                  <span className="text-xs">{comments.length}</span>
                )}
              </button>

              {/* Repost Button */}
              <button
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-spiders-text-muted hover:text-spiders-success hover:bg-spiders-success/10 transition-colors mr-1"
                onClick={handleRepost}
              >
                <FaRetweet className="w-4 h-4" />
                {message.reposts?.length > 0 && (
                  <span className="text-xs">{message.reposts.length}</span>
                )}
              </button>

              {/* Like Button */}
              <div className={`heart-burst-container ${isAnimating ? 'animating' : ''}`}>
                <button
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all mr-1 ${
                    isLiked
                      ? "text-spiders-error bg-spiders-error/10"
                      : "text-spiders-text-muted hover:text-spiders-error hover:bg-spiders-error/10"
                  } ${isAnimating ? "heart-burst" : ""}`}
                  onClick={handleLike}
                >
                  {isLiked ? <FaHeart className="w-4 h-4" /> : <FaRegHeart className="w-4 h-4" />}
                  {message.likes?.length > 0 && (
                    <span className="text-xs">{message.likes.length}</span>
                  )}
                </button>
              </div>

              {/* Share Button */}
              <button
                className="flex items-center px-3 py-1.5 rounded-full text-spiders-text-muted hover:text-spiders-accent-400 hover:bg-spiders-accent-400/10 transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                <FaShare className="w-4 h-4" />
              </button>
            </div>

            {/* Commenter avatar stack (social proof) */}
            {uniqueCommenters.length >= 2 && !showComments && (
              <button
                className="flex items-center mt-2 space-x-2"
                onClick={handleExpandComments}
              >
                <div className="flex -space-x-2">
                  {uniqueCommenters.slice(0, 5).map((commenter) => (
                    <img
                      key={commenter.username}
                      src={getProfilePicture(commenter.username, commenter.avatarSeed)}
                      alt={commenter.username}
                      className="w-6 h-6 rounded-full border-2 border-spiders-bg-surface"
                    />
                  ))}
                </div>
                <span className="text-xs text-spiders-text-muted">
                  {comments.length} {comments.length === 1 ? 'reply' : 'replies'}
                  {uniqueCommenters.length > 5 && ` from ${uniqueCommenters.length}+ people`}
                </span>
              </button>
            )}

            {/* Show replies link (only in feed view when there are comments) */}
            {!showAllByDefault && comments.length > 0 && !showComments && uniqueCommenters.length < 2 && (
              <button
                className="mt-1 text-spiders-text-muted hover:text-spiders-primary-400 text-sm transition-colors"
                onClick={handleExpandComments}
              >
                Show this thread
              </button>
            )}

            {/* Hide replies button */}
            {!showAllByDefault && showComments && (
              <button
                className="mt-1 text-spiders-text-muted hover:text-spiders-primary-400 text-sm transition-colors flex items-center space-x-1"
                onClick={handleExpandComments}
              >
                <FaChevronUp className="w-3 h-3" />
                <span>Hide replies</span>
              </button>
            )}

            {/* "View all X replies" link for feed mode when there are more comments */}
            {feedMode && showComments && !showAllComments && organizedComments.length > FEED_COMMENT_PREVIEW && (
              <button
                className="mt-1 text-spiders-primary-400 hover:text-spiders-primary-300 text-sm transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/messages/${message._id}?condition=${condition}`);
                }}
              >
                View all {comments.length} replies &rarr;
              </button>
            )}
          </div>
        </div>

        {/* Comment Input — shown ABOVE comments for visibility */}
        {showCommentInput && (
          <div className="ml-[52px] pt-3 pb-2 border-b border-spiders-border/50" onClick={(e) => e.stopPropagation()}>
            <div className="flex space-x-3">
              <img
                src={getProfilePicture(user.username, user.avatarSeed)}
                alt={user.username}
                className="w-8 h-8 rounded-full flex-shrink-0"
              />
              <div className="flex-1">
                <MentionInput
                  value={newComment}
                  onChange={setNewComment}
                  placeholder={`Reply to @${message.author?.username}...`}
                  rows={2}
                  className="text-sm"
                  mentionableUsers={mentionableUsers}
                />
                <div className="flex items-center justify-between mt-2">
                  <button
                    type="button"
                    onClick={() => setShowCommentEmojis(prev => !prev)}
                    className={`p-2 rounded-full transition-all duration-200 ${
                      showCommentEmojis
                        ? 'text-spiders-primary-400 bg-spiders-primary-500/20'
                        : 'text-spiders-accent-400 hover:bg-spiders-accent-500/20 hover:text-spiders-accent-300'
                    }`}
                    title="Add emoji"
                  >
                    <FaSmile className="w-4 h-4" />
                  </button>
                  <button
                    className="spiders-btn-primary text-sm py-1.5 px-4 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleComment}
                    disabled={!newComment.trim()}
                  >
                    Reply
                  </button>
                </div>
                {showCommentEmojis && (
                  <EmojiPicker
                    onSelect={insertCommentEmoji}
                    onClose={() => setShowCommentEmojis(false)}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Comments Section (hidden by default in feed, shown on detail page) */}
        {showComments && comments.length > 0 && (
          <div className="ml-[52px] border-l border-spiders-border pl-4 pb-2" onClick={(e) => e.stopPropagation()}>
            {displayedComments.map((comment) => renderComment(comment))}

            {organizedComments.length > INITIAL_COMMENT_DISPLAY && (
              <button
                className="text-spiders-primary-400 hover:text-spiders-primary-300 text-sm mt-2 flex items-center space-x-1"
                onClick={toggleComments}
              >
                {showAllComments ? (
                  <>
                    <FaChevronUp className="w-3 h-3" />
                    <span>Show less</span>
                  </>
                ) : (
                  <>
                    <FaChevronDown className="w-3 h-3" />
                    <span>Show more replies</span>
                  </>
                )}
              </button>
            )}

            {/* Typing indicator */}
            <TypingIndicator messageId={message._id} />
          </div>
        )}

        {/* Typing indicator when comments are collapsed */}
        {!showComments && (
          <div className="ml-[52px] pl-4" onClick={(e) => e.stopPropagation()}>
            <TypingIndicator messageId={message._id} />
          </div>
        )}
      </div>

      {/* Repost Modal */}
      {showRepostModal && (
        <div className="fixed inset-0 bg-spiders-bg-dark/80 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={(e) => e.stopPropagation()}>
          <div className="spiders-card p-6 w-full max-w-lg shadow-2xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-spiders-text-primary">Repost</h3>
              <button
                onClick={() => setShowRepostModal(false)}
                className="text-spiders-text-muted hover:text-spiders-text-primary transition-colors p-2 rounded-full hover:bg-spiders-bg-elevated"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
            <div className="bg-spiders-bg-elevated p-4 rounded-xl mb-4 border border-spiders-border">
              <div className="flex items-center space-x-2 mb-2">
                <img
                  src={getProfilePicture(message.author?.username || "unknown", message.author?.avatarSeed)}
                  alt={message.author?.username}
                  className="w-6 h-6 rounded-full"
                />
                <span className="font-medium text-spiders-text-primary text-sm">
                  {message.author?.username}
                </span>
                <span className="text-spiders-text-muted text-xs">
                  @{message.author?.username}
                </span>
              </div>
              <p className="text-spiders-text-secondary text-sm">{renderTextWithLinks(message.content)}</p>
            </div>
            <textarea
              className="spiders-input w-full resize-none mb-4"
              placeholder="Add a comment (optional)..."
              value={repostComment}
              onChange={(e) => setRepostComment(e.target.value)}
              rows="3"
            />
            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 text-spiders-text-muted hover:text-spiders-text-primary transition-colors"
                onClick={() => setShowRepostModal(false)}
              >
                Cancel
              </button>
              <button
                className="spiders-btn-primary"
                onClick={submitRepost}
              >
                Repost
              </button>
            </div>
          </div>
        </div>
      )}
    </article>
  );
};

export default Message;
